
import React, { createContext, useContext } from 'react';
import { AuthContextType } from './types';
import { useAuthState } from './useAuthState';
import { useLogin } from './useLogin';
import { useLogout } from './useLogout';
import { useProfileUpdate } from './useProfileUpdate';
import { useEmailVerification } from './useEmailVerification';
import { checkInitialSetupRequired } from './services/setupService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Combine all the hooks to create the auth context
  const { user, setUser, token, setToken, isLoading, isAuthenticated } = useAuthState();
  const { login } = useLogin(setUser, setToken);
  const { logout } = useLogout(setUser, setToken);
  const { updateProfile } = useProfileUpdate(user, token, setUser);
  const { confirmEmail, resendVerification } = useEmailVerification();

  // Registration is now handled by components with Router access
  // This is just a placeholder for type compatibility
  const register = async () => {
    console.warn('Registration should be called from components within Router context');
    return { success: false, errors: { general: 'Registration not available in this context' } };
  };

  const checkSetupRequired = async (): Promise<boolean> => {
    return await checkInitialSetupRequired();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated,
        login,
        register,
        logout,
        updateProfile,
        confirmEmail,
        resendVerification,
        checkSetupRequired,
        errors: {},
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
